import React, {useState, useEffect, useRef} from 'react';
import { Alert, CircularProgress, Stack, Divider, OutlinedInput, InputAdornment, Grid2 as Grid, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import Main from '../../../layouts/Main';
import Container from '../../../components/Container';
import PageBreadcrumbs from '../../../components/PageBreadcrumbs';
import ReportListExportSession from './ReportListExportSection';
import IndependentSpendingReportsTable from './IndependentSpendingReportsTable';
import { useErrorBoundary } from 'react-error-boundary';
import MiscReportContent from '../../../types/reports/miscReportContent';
import { debounce } from 'lodash';


const ElectioneeringCommunicationsReports = (): JSX.Element => {
  const dropDownYears = Array.from(new Array(11), (val, index) => new Date().getFullYear() - index);
  const [reportYear, setReportYear] = useState(new Date().getMonth()===0?new Date().getFullYear()-1: new Date().getFullYear());
  const [reports, setReports] = useState<MiscReportContent[]>([]);
  const [tableRows, setTableRows] = useState<MiscReportContent[]>([]);
  const [filersDropdown, setFilersDropdown] = useState([]);
  const [candidatesDropdown, setCandidatesDropdown] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [currentFiler, setCurrentFiler] = useState('All');
  const [currentCandidate, setCurrentCandidate] = useState('All');
  const [summary, setSummary] = useState('No reports found.');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const startInputRef = useRef<HTMLInputElement>(null);
  const endInputRef = useRef<HTMLInputElement>(null);

  const { showBoundary } = useErrorBoundary(); 

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setReportYear(new Date().getMonth()===0?new Date().getFullYear()-1: new Date().getFullYear());
    setCurrentFiler('All');
    setCurrentCandidate('All');
  };

  const updateFiler = (event: any) => {
    setCurrentFiler(event.target.value);

  };

  const updateCandidate = (event: any) => {
    setCurrentCandidate(event.target.value);
  };

  const updateReportYear = (event: any) => {
    setReportYear(event.target.value);

  };

  const filterDate = debounce((event: any) => {
    const id = event.target.id;  // Capture values immediately
    const value = event.target.value;

    id === 'startDate' ? setStartDate(value) : setEndDate(value);
  }, 800);

  const getSummary = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/miscreports/reports/summary/${reportYear}?reportTypeDescription=EC`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get EC reports summary.'));
          }
          return result.json();
        })
        .then(result => {
          let count = result.count;
          if (count === 0) {
            setSummary("No reports found.");
            return;
          }
          else {
            setSummary(`Viewing 1 through ${count} of ${count} reports, ${result.totalDisplay}`);
          }


        });
    } catch (error) {
      console.error('Uncaught Error:', error);
    }
  }

  const getFilersDropdown = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/miscreports/entities/${reportYear}?reportType=EC`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get the Independent Expenditure filers.'));
          }
          return result.json();
        })
        .then(result => {
          setFilersDropdown(result);
        });
    } catch (error) {
      console.error('Uncaught Error:', error);
    }
  }


  const getCandidatesDropdown = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/miscreports/candidates/${reportYear}?reportTypeDescription=EC`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get the EC candidates.'));
          }
          return result.json();
        })
        .then(result => {
          setCandidatesDropdown(result);
        });
    } catch (error) {
      console.error('Uncaught Error:', error);
    }
  }

  const getElectioneeringCommunicationsReports = async () => {
    try{
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/miscreports/reports/${reportYear}?reportTypeDescription=EC`)
      .then(result => {
        if (!result.ok) {
          showBoundary(new Error('Failed to get electioneering communication reports.'));
        }
        return result.json();
      })
      .then(result => {
        setReports(result);
        setTableRows(result);
        getSummary();
        setIsLoading(false);
      });
    }catch(error){
        console.error('Uncaught Error:', error);
    }
  }

  const handleFilterChange = () => {
 
      const filtered = reports?.filter((row:MiscReportContent) => {
     

        const matchesFiler = currentFiler === 'All' || row.filingEntity.includes(currentFiler);
        const matchesCandidate = currentCandidate === 'All' || row.candidateListing.includes(currentCandidate);
  
    
        let matchesStartDate = true;
        let matchesEndDate = true;
  
        if (startDate.length > 1 && (new Date(row.date) < new Date(startDate))) {
          matchesStartDate = false;
        }
  
        if (endDate.length > 1 && (new Date(row.date) > new Date(endDate))) {
          matchesEndDate = false;
        }
        
      
  
        return matchesFiler && matchesCandidate && matchesStartDate && matchesEndDate;
      });  
      setTableRows(filtered);
      if (filtered) {
        filtered.length > 0 ? setSummary(`Viewing 1 through ${filtered.length} of ${reports?.length} reports`) : setSummary("No reports found.");
      } else {
        setSummary("No reports found.");
      }
    

  };

  

  useEffect(() => {
    getElectioneeringCommunicationsReports();
    getFilersDropdown();
    getCandidatesDropdown();
  }, [reportYear]);

  useEffect(() => {
    handleFilterChange();
  }, [currentFiler, currentCandidate, startDate, endDate]);

  return (
    <Main>
      <Container>
        <PageBreadcrumbs></PageBreadcrumbs>
        <Typography
          variant="h5"
          align={'left'}
          data-aos={'fade-up'}
          sx={{
            fontWeight: 700,
          }}
          paddingTop={3}
          gutterBottom
          marginBottom={2}
        >
          Electioneering Communications
        </Typography>
        <Divider />
        <Grid container marginBottom={2} marginTop={3}>
          <Grid size={12} marginBottom={4}>
            <Typography marginBottom={1} sx={{ textIndent: 10 }} component={'p'} variant='body1'>The Massachusetts campaign finance law requires the disclosure of expenditures made by individuals, groups and organizations, other than registered political committees, who make or contract to make electioneering communication expenditures of more than $250 (aggregated) in a calendar year.</Typography>
            <Typography marginBottom={1} sx={{ textIndent: 10 }} component={'p'} variant='body1'>Electioneering Communication expenditure reports are displayed on this page in chronological order, newest to oldest. The reports can be filtered by year, by making a selection from the dropdown list provided. Within each year, the reports can be filtered by a date range, the individual or entity making the expenditure, the subject (candidate) of the expenditure, or the position (support or oppose) taken by the filer.</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 10 }}>

            <Stack direction={"row"} spacing={1}
              sx={{ mb: 1 }}
            >

              <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                <InputLabel id="year-id">Year</InputLabel>
                <Select
                  sx={{ fontSize: 'smaller' }}
                  labelId="year-id"
                  id="year-select"
                  aria-labelledby='year-id'
                  label="Report Year"
                value={reportYear}
                onChange={updateReportYear}
                >
                  {
                    dropDownYears.map((year) =>
                      <MenuItem value={year} key={year} >{year}</MenuItem>
                    )
                  }
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ m: 1, p: 0, maxWidth: 100 }}>
                <OutlinedInput
                  aria-label='start date'
                  placeholder={"Start Date"}
                  inputRef={startInputRef}
                  id="startDate"
                  type='text'
                  onChange={filterDate}
                  sx={{ fontSize: 'smaller' }}
                />

              </FormControl>
              <FormControl size="small" sx={{ m: 1, p: 0, maxWidth: 100 }}>
                <OutlinedInput
                aria-label='end date'
                  placeholder={"End Date"}
                  inputRef={endInputRef}
                  type='text'
                  id="endDate"
                  onChange={filterDate}
                  sx={{ fontSize: 'smaller' }}
                />

              </FormControl>

              <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                <InputLabel id="filer-id">Filer</InputLabel>
                <Select
                  labelId="filer-id"
                  id="filer-select"
                  aria-labelledby='filer-id'
                  sx={{ fontSize: 'smaller' }}
                  label="Type"
                value={currentFiler}
                onChange={updateFiler}
                >
                  <MenuItem value='All' key='All' >All</MenuItem>
                  {
                    
                    filersDropdown.map((filer) =>
                      <MenuItem value={filer} key={filer} >{filer}</MenuItem>
                    )
                  }

                </Select>
              </FormControl>

              <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                <InputLabel id="candidate-id">Candidate</InputLabel>
                <Select
                  labelId="candidate-id"
                  id="candidate-select"
                  aria-labelledby='candidate-id'
                  sx={{ fontSize: 'smaller' }}
                  label="Type"
                value={currentCandidate}
                onChange={updateCandidate}
                >
                  <MenuItem value='All' key='All' >All</MenuItem>
                  {
                    candidatesDropdown.map((candidate) =>
                      <MenuItem value={candidate} key={candidate} >{candidate}</MenuItem>
                    )
                  }
                </Select>
              </FormControl>

            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            {/* <ReportListExportSession></ReportListExportSession> */}
          </Grid>
          <Grid size={12}>
            <Alert icon={false} severity="info"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                '.MuiAlert-message': {
                  padding: '4px',
                }
              }}
            >
              {summary}
            </Alert>
          </Grid>
        </Grid>

        <IndependentSpendingReportsTable tableRows={tableRows} type='ecReports'></IndependentSpendingReportsTable>
        {/*loading indicator*/}
               <CircularProgress
               aria-label='loading'
                    size={50}
                    className={isLoading? "": "hidden"}
                    sx={{
                      position: "fixed",
                      left: "50%",
                      top: "20%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 2
                     }}
                />
      </Container>
    </Main>
  );
};

export default ElectioneeringCommunicationsReports;